import { SubscriptionType } from "@/types/enums/SubscriptionType";
import { SubscriptionScope } from "@/types/enums/SubscriptionScope";
import { Subscription } from "@/types/Subscription";
import type { Company } from "@/types/Company";

const PROFESSIONAL_TYPES = new Set<SubscriptionType>([
  SubscriptionType.PROFESSIONAL,
  SubscriptionType.PROFESSIONAL_YEAR,
  SubscriptionType.EVENT_PROFESSIONAL,
]);

const ADVANCED_PLUS_TYPES = new Set<SubscriptionType>([
  SubscriptionType.ADVANCED,
  SubscriptionType.ADVANCED_YEAR,
  SubscriptionType.EVENT_ADVANCED,
  SubscriptionType.PROFESSIONAL,
  SubscriptionType.PROFESSIONAL_YEAR,
  SubscriptionType.EVENT_PROFESSIONAL,
]);

export function canUseCustomDomain(subscriptions: Subscription[]): boolean {
  return subscriptions.some((s) => s.type && PROFESSIONAL_TYPES.has(s.type));
}

export function canUseCustomEmailDomain(
  subscriptions: Subscription[],
): boolean {
  return subscriptions.some((s) => s.type && PROFESSIONAL_TYPES.has(s.type));
}

export function canUseWhiteLabeling(subscriptions: Subscription[]): boolean {
  return subscriptions.some((s) => s.type && ADVANCED_PLUS_TYPES.has(s.type));
}

export function canUploadCustomWebsite(subscriptions: Subscription[]): boolean {
  return subscriptions.some((s) => s.type && PROFESSIONAL_TYPES.has(s.type));
}

/**
 * COMPANY-wide Professional gate (2026-08-01).
 *
 * The backend's company-wide gates (portal custom domain, company-scope email
 * domain) now require a COMPANY-scope subscription: an EVENT_PROFESSIONAL buys
 * features for its own event, not for the tenant. The other helpers here stay
 * scope-blind on purpose — they gate EVENT-level features, where the event's
 * own plan legitimately counts.
 *
 * Callers must request `scope` in the fetchSubscriptions projection; without it
 * every row reads as non-company and the UI hides a feature the user has.
 */
export function canUseCompanyWideCustomDomain(subscriptions: Subscription[]): boolean {
  return subscriptions.some(
    (s) =>
      s.scope === SubscriptionScope.COMPANY &&
      s.type &&
      (s.type === SubscriptionType.PROFESSIONAL || s.type === SubscriptionType.PROFESSIONAL_YEAR),
  );
}

/**
 * Whether the tenant may still SEE and DELETE a company-wide domain it already
 * has — as opposed to setting up a new one, which is
 * {@link canUseCompanyWideCustomDomain} and always needs a real company plan.
 *
 * The distinction exists because tightening the gate does not merely hide a
 * feature: main-consumer's downgrade teardown deletes the Cloudflare custom
 * hostname and clears the Mongo fields, and getting it back means re-verifying
 * DNS. Tenants who configured a domain while the old, scope-blind gate accepted
 * an event plan carry `legacyDomainEntitlement` from a backfill, and both
 * backends now spare them. Without this helper the UI would hide a domain that
 * is still live and still billed — the customer could neither see nor remove it.
 *
 * Callers must request `legacyDomainEntitlement` in the getCompany projection;
 * the field is projection-based, so omitting it reads as "not grandfathered".
 */
export function canManageExistingCompanyWideDomain(
  subscriptions: Subscription[],
  company: Pick<Company, "legacyDomainEntitlement"> | undefined,
): boolean {
  return (
    canUseCompanyWideCustomDomain(subscriptions) || company?.legacyDomainEntitlement === true
  );
}

/**
 * Event-scope Professional gate that mirrors the backend's event branch
 * (2026-08-02): THIS event's own plan, or the company's own COMPANY-scope plan.
 *
 * The scope-blind `canUseCustomEmailDomain` also counted a SIBLING event's
 * EVENT_PROFESSIONAL, which the backend stopped accepting — so the UI offered a
 * setup the server then rejected with 403. Callers must request `scope,eventId`
 * in the projection.
 */
export function canUseEventCustomEmailDomain(
  subscriptions: Subscription[],
  eventId: string,
): boolean {
  return subscriptions.some((s) => {
    if (!s.type || !PROFESSIONAL_TYPES.has(s.type)) return false;
    if (s.scope === SubscriptionScope.COMPANY) return true;
    return s.eventId === eventId;
  });
}
